import { Component, Input, OnInit } from '@angular/core';
import {
  DashboardEngineiusKPIComponent,
  MetricDisplayStyle,
} from './dashboard-engineius-kpi.component';

enum ChartMetric {
  CompletedMoves,
  TotalRevenue,
  AverageJobValue,
  TotalExternalCostsPerMovement,
  GrossProfitValue,
  GrossProfitPercentage,
  GrossProfitPerMovement,
}

@Component({
  selector: 'dashboard-engineius-financial-kpi',
  styleUrls: ['./dashboard-engineius-kpi.component.scss'],
  templateUrl: './dashboard-engineius-kpi.component.html',
})
export class DashboardEngineiusKPIFinancialComponent extends DashboardEngineiusKPIComponent {
  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.selectedMonthRange = 12;

    // load chart data
    await this.loadData();
  }

  async loadData() {
    console.log('Loading Commercial chart data');

    await super.loadData();

    this.monthlyTotals = await this.kpiService.getKPIFinancialTotals(
      this.kpiStartDate,
      this.selectedMonthRange,
    );

    this.setMonthDates(this.selectedMonthRange);

    this.completedTotals = this.monthlyTotals.filter((x) => !x.isCancelled);
    console.log('Financial totals: ', this.completedTotals);

    this.totalsChartData = [];

    const dataMap = new Map<number, number[]>([
      [
        ChartMetric.CompletedMoves,
        this.completedTotals
          .filter((x) => !x.isCancelled)
          .map((x) => {
            return x.totalCompleted;
          }),
      ],
      [
        ChartMetric.TotalRevenue,
        this.completedTotals.map((x) => {
          return x.totalRevenue;
        }),
      ],
      [
        ChartMetric.AverageJobValue,
        this.completedTotals.map((x) => {
          return x.averageJobValue;
        }),
      ],
      [
        ChartMetric.TotalExternalCostsPerMovement,
        this.completedTotals.map((x) => {
          return x.totalExternalCostsPerMovement;
        }),
      ],
      [
        ChartMetric.GrossProfitValue,
        this.completedTotals.map((x) => {
          return x.grossProfitValue;
        }),
      ],
      [
        ChartMetric.GrossProfitPercentage,
        this.completedTotals.map((x) => {
          return x.grossProfitPercentage;
        }),
      ],
      [
        ChartMetric.GrossProfitPerMovement,
        this.completedTotals.map((x) => {
          return x.grossProfitPerMovement;
        }),
      ],
    ]);

    this.totalsChartData = Array.from(dataMap.values());
    //console.log("Commercial Totals chart data: ", this.totalsChartData);

    // create metric card data for each kpi measure
    this.monthlyMetrics = [];
    this.createChart_TotalRevenue();
    this.createChart_TotalExternalCostsPerMovement();
    this.createChart_AverageJobValue();
    this.createChart_GrossProfitValue();
    this.createChart_GrossProfitPercentage();
    this.createChart_GrossProfitPerMovement();

    this.isLoading = false;

    console.log('Commercial Metrics: ', this.monthlyMetrics);
  }

  setCurrencyTotal(metric, dataIndex) {
    const data = this.totalsChartData[dataIndex];
    const total = data.reduce((x, y) => x + y);
    metric.chartData.totalCurrency = total;
    metric.noCompPerc = true;
  }

  createChart_TotalRevenue() {
    let metrics = [];

    let metric = this.getMetricData_v2(
      'Total Revenue',
      ChartMetric.TotalRevenue,
      false,
      false,
      false,
      MetricDisplayStyle.Currency,
    );
    let chartData = this.getTwoSetChartData(
      ChartMetric.TotalRevenue,
      ChartMetric.CompletedMoves,
      'rgba(10, 220, 85, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      'Total Revenue',
      'CompletedMoves',
      null,
      'rgba(255, 255, 255, 1)',
      'bar',
      'line',
      this.twoSetChartCurrencyOptions,
    );
    //.getCurrencyChartData(ChartMetric.TotalRevenue, 'rgba(10, 220, 85, 0.5)', "Revenue");
    metric.chartData = chartData;
    metric.tooltip =
      'Total revenue associated with customer orders / movements including all fees and expenses';
    this.setCurrencyTotal(metric, ChartMetric.TotalRevenue);

    metric.totalName = 'Revenue';
    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Total Revenue', metrics });
  }

  createChart_TotalExternalCostsPerMovement() {
    let metrics = [];

    let metric = this.getMetricData_v2(
      'Supplier Costs',
      ChartMetric.TotalExternalCostsPerMovement,
      false,
      false,
      false,
      MetricDisplayStyle.Currency,
    );
    //let chartData = this.getCurrencyChartData(ChartMetric.TotalExternalCostsPerMovement, 'rgba(50, 168, 149, 0.9)', "Supplier Costs");
    let chartData = this.getTwoSetChartData(
      ChartMetric.TotalExternalCostsPerMovement,
      ChartMetric.CompletedMoves,
      'rgba(50, 168, 149, 0.9)',
      'rgba(66, 164, 245, 0.5)',
      'Supplier Costs',
      'CompletedMoves',
      null,
      null,
      'bar',
      'line',
      this.twoSetChartCurrencyOptions,
    );
    metric.chartData = chartData;
    metric.tooltip =
      'Total supplier costs associated with supplier jobs including all incentives and expenses';
    this.setCurrencyTotal(metric, ChartMetric.TotalExternalCostsPerMovement);

    metric.totalName = 'Supplier Costs';
    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Supplier Costs', metrics });
  }

  createChart_AverageJobValue() {
    let metrics = [];

    let metric = this.getMetricData_v2(
      'Average Job Value',
      ChartMetric.AverageJobValue,
      false,
      false,
      false,
      MetricDisplayStyle.Currency,
    );
    //let chartData = this.getCurrencyChartData(ChartMetric.AverageJobValue, 'rgba(0, 128, 203, 0.7)', "Job Value");
    let chartData = this.getTwoSetChartData(
      ChartMetric.AverageJobValue,
      ChartMetric.CompletedMoves,
      'rgba(0, 128, 203, 0.7)',
      'rgba(66, 164, 245, 0.5)',
      'Average Job Value',
      'CompletedMoves',
      null,
      null,
      'bar',
      'line',
      this.twoSetChartCurrencyOptions,
    );

    metric.chartData = chartData;
    metric.tooltip =
      'Average revenue per customer order / movement including all fees and expenses';
    this.setCurrencyTotal(metric, ChartMetric.AverageJobValue);

    metric.totalName = 'Job Value';
    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Average Job Value', metrics });
  }

  createChart_GrossProfitValue() {
    let metrics = [];

    let metric = this.getMetricData_v2(
      'Gross Profit Value',
      ChartMetric.GrossProfitValue,
      false,
      false,
      false,
      MetricDisplayStyle.Currency,
    );
    //let chartData = this.getCurrencyChartData(ChartMetric.GrossProfitValue, 'rgba(10, 220, 85, 0.5)', "Gross Profit");
    let chartData = this.getTwoSetChartData(
      ChartMetric.GrossProfitValue,
      ChartMetric.CompletedMoves,
      'rgba(10, 220, 85, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      'Gross Profit',
      'CompletedMoves',
      null,
      null,
      'bar',
      'line',
      this.twoSetChartCurrencyOptions,
    );
    metric.chartData = chartData;
    this.setCurrencyTotal(metric, ChartMetric.GrossProfitValue);

    metric.tooltip = 'Gross profit divided by the number of tasks';
    metric.totalName = 'Gross Profit';
    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Gross Profit', metrics });
  }

  createChart_GrossProfitPercentage() {
    let metrics = [];

    let metric = this.getMetricData_v2(
      'Gross Profit Percentage',
      ChartMetric.GrossProfitPercentage,
      false,
      true,
      false,
      MetricDisplayStyle.Percentage,
    );
    let chartData = this.getPercentageChartData(
      ChartMetric.GrossProfitPercentage,
      'Gross Profit Percent',
      'rgba(0, 128, 203, 0.7)',
      'line',
      false,
    );
    metric.chartData = chartData;
    metric.noComp = true;
    metric.tooltip = 'Gross profit divided by the number of tasks as %';

    metric.totalName = 'Gross Profit';
    metric.useValueAsPercent = true;
    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Gross Profit Percentage', metrics });
  }

  createChart_GrossProfitPerMovement() {
    let metrics = [];

    let metric = this.getMetricData_v2(
      'Gross Profit Per Movement',
      ChartMetric.GrossProfitPerMovement,
      false,
      false,
      false,
      MetricDisplayStyle.Currency,
    );
    //let chartData = this.getCurrencyChartData(ChartMetric.GrossProfitPerMovement, 'rgba(10, 220, 85, 0.5)', "Gross Profit Per Move");
    let chartData = this.getTwoSetChartData(
      ChartMetric.GrossProfitPerMovement,
      ChartMetric.CompletedMoves,
      'rgba(10, 220, 85, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      'Gross Profit Per Move',
      'CompletedMoves',
      null,
      null,
      'bar',
      'line',
      this.twoSetChartCurrencyOptions,
    );
    metric.chartData = chartData;
    this.setCurrencyTotal(metric, ChartMetric.GrossProfitPerMovement);

    metric.tooltip = 'Gross Profit Per Movement';
    metric.totalName = 'Gross Profit Per Movement';
    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Gross Profit Per Movement', metrics });
  }
}
